/**
 * Type-safe authorization action constants
 *
 * These constants provide compile-time safety for permission checks.
 * Use with useProjectAuth/useWorkflowAuth hooks instead of magic strings.
 *
 * @example
 * ```typescript
 * import { WORKFLOW_ACTIONS } from "@/constants/auth";
 * import { useWorkflowAuth } from "@/hooks/useWorkflowAuth";
 *
 * const { can } = useWorkflowAuth(workflow.auth);
 * if (can(WORKFLOW_ACTIONS.UPDATE)) { ... }
 * ```
 */

/** Project-level authorization actions */
export const PROJECT_ACTIONS = {
  UPDATE: "updateProject",
  DELETE: "deleteProject",
  SHARE: "shareProject",
  VIEW_SHARING: "viewProjectSharing",
  ARCHIVE: "archiveProject",
  MANAGE_OWNERS: "manageProjectOwners",
  MANAGE_EDITORS: "manageProjectEditors",
} as const;

/** Workflow-level authorization actions */
export const WORKFLOW_ACTIONS = {
  UPDATE: "updateWorkflow",
  DELETE: "deleteWorkflow",
  EXECUTE: "executeWorkflow",
  DUPLICATE: "duplicateWorkflow",
} as const;

/** Type for project action values */
export type ProjectAction =
  (typeof PROJECT_ACTIONS)[keyof typeof PROJECT_ACTIONS];

/** Type for workflow action values */
export type WorkflowAction =
  (typeof WORKFLOW_ACTIONS)[keyof typeof WORKFLOW_ACTIONS];

/** Organisation-level authorization actions */
export const ORG_ACTIONS = {
  IS_OWNER: "isOrganisationOwner",
  IS_EDITOR: "isOrganisationEditor",
  CREATE: "createOrganisation",
  READ: "readOrganisation",
  UPDATE: "updateOrganisation",
  DELETE: "deleteOrganisation",
  SUSPEND: "suspendOrganisation",
  RESTORE: "restoreOrganisation",
  MANAGE_SHARING: "manageSharingDomainSettings",
  READ_USAGE: "readOrganisationUsage",
  READ_MEMBER: "readOrganisationMember",
  ADD_MEMBER: "addOrganisationMember",
  UPDATE_MEMBER: "updateOrganisationMember",
  REMOVE_MEMBER: "removeOrganisationMember",
  CREATE_TEAM: "createTeam",
} as const;

/** Type for organisation action values */
export type OrgAction = (typeof ORG_ACTIONS)[keyof typeof ORG_ACTIONS];

/** Team-level authorization actions */
export const TEAM_ACTIONS = {
  CREATE: "createTeam",
  READ: "readTeam",
  UPDATE: "updateTeam",
  DELETE: "deleteTeam",
  ARCHIVE: "archiveTeam",
  LEAVE: "leaveTeam",
  CREATE_MEMBER: "createTeamMember",
  UPDATE_MEMBER: "updateTeamMember",
  DELETE_MEMBER: "deleteTeamMember",
} as const;

/** Type for team action values */
export type TeamAction = (typeof TEAM_ACTIONS)[keyof typeof TEAM_ACTIONS];

/** Email settings authorization actions */
export const EMAIL_ACTIONS = {
  MANAGE_CONTACTS: "manageContactEmail",
  MANAGE_PREFERENCES: "manageEmailPreference",
  ADD_CONTACT_EMAIL: "addContactEmail",
} as const;

/** Contact email state attributes (per-item auth from contact status) */
export const CONTACT_EMAIL_STATES = {
  IS_PENDING: "isContactEmailPending",
  IS_VERIFIED: "isContactEmailVerified",
  IS_ACTIVE: "isContactEmailActive",
  IS_SUSPENDED: "isContactEmailSuspended",
  IS_DELETED: "isContactEmailDeleted",
  IS_EXPIRED: "isContactEmailExpired",
} as const;

/** Type for email action + state attribute values */
export type EmailAction =
  | (typeof EMAIL_ACTIONS)[keyof typeof EMAIL_ACTIONS]
  | (typeof CONTACT_EMAIL_STATES)[keyof typeof CONTACT_EMAIL_STATES];
